// src/pages/Admin/Abonnement/AbonnementPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import useScrollToTop from "../../../hooks/useScollToTop";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { FaSync } from "react-icons/fa";
import ParrainageSection from "./ParrainageSection";
import RequestsAdmin from "./../RequestsAdmin/RequestsAdmin";

const GRADIENT =
  "bg-gradient-to-r from-sky-600 via-sky-500 to-cyan-400 text-white";

const euro = (n) =>
  typeof n === "number"
    ? n.toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      })
    : n;

const parseDateSafe = (d) => {
  if (!d) return null;
  // si format YYYY-MM-DD => date locale à minuit
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d)
    ? new Date(`${d}T00:00:00`)
    : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

const dayAnchor = (d) => {
  // ancre à midi pour éviter des effets DST/midnight
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  return x;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = parseDateSafe(d);
  if (!dt) return "—";
  return dt.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const normalizeKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const statusBadge = (s) => {
  // 🔁 mapping robuste (API peut renvoyer FR/EN)
  const raw = String(s || "").trim();
  const key = normalizeKey(raw);

  if (!key) return { label: "—", cls: "bg-slate-500" };

  // Abonné (vert)
  if (
    key === "active" ||
    key === "abonne" ||
    key.includes("abonn") ||
    key === "subscribed"
  ) {
    return { label: "Abonné", cls: "bg-emerald-600" };
  }

  // En attente (orange)
  if (key === "pending" || key.includes("attente")) {
    return { label: "En attente", cls: "bg-amber-500" };
  }

  // Terminé / Expiré / Annulé (rouge)
  if (
    key === "expired" ||
    key.includes("expire") ||
    key.includes("termine") ||
    key.includes("annule") ||
    key === "cancelled" ||
    key === "canceled"
  ) {
    return { label: "Terminé", cls: "bg-rose-600" };
  }

  // autres (conservés si tu en as)
  if (key.includes("pause")) return { label: "En pause", cls: "bg-violet-600" };
  if (key.includes("progress") || key.includes("cours"))
    return { label: "En cours", cls: "bg-blue-600" };

  return { label: raw || "—", cls: "bg-slate-500" };
};

const periodicityBadge = (p) => {
  const key = String(p || "").toLowerCase();
  const map = {
    yearly: { label: "Annuel", cls: "bg-sky-600" },
    annual: { label: "Annuel", cls: "bg-sky-600" },
    monthly: { label: "Mensuel", cls: "bg-indigo-600" },
    trimestrial: { label: "Trimestriel", cls: "bg-cyan-600" },
    quarterly: { label: "Trimestriel", cls: "bg-cyan-600" },
  };
  return map[key] || { label: p || "—", cls: "bg-slate-600" };
};

// ✅ jauge : vert -> orange à J-30 -> rouge si dépassé
const computeProgress = (start_date, end_date) => {
  const s0 = parseDateSafe(start_date);
  const e0 = parseDateSafe(end_date);

  if (!s0 || !e0) {
    return {
      valid: false,
      percent: 0,
      daysRemaining: null,
      colorCls: "bg-slate-400",
      text: "—",
    };
  }

  const today = dayAnchor(new Date());
  const s = dayAnchor(s0);
  const e = dayAnchor(e0);

  const totalMs = e.getTime() - s.getTime();
  const totalDays = totalMs > 0 ? totalMs / 86400000 : 0;

  // jours restants (si expiré => négatif ou 0)
  const daysRemaining = Math.ceil((e.getTime() - today.getTime()) / 86400000);

  // pourcentage écoulé
  let percent = 0;
  if (totalDays <= 0) {
    percent = today.getTime() >= e.getTime() ? 100 : 0;
  } else if (today.getTime() <= s.getTime()) {
    percent = 0;
  } else if (today.getTime() >= e.getTime()) {
    percent = 100;
  } else {
    percent = ((today.getTime() - s.getTime()) / totalMs) * 100;
  }
  percent = clamp(Math.round(percent), 0, 100);

  // couleur jauge
  let colorCls = "bg-emerald-600";
  if (today.getTime() > e.getTime()) colorCls = "bg-rose-600";
  else if (daysRemaining <= 30) colorCls = "bg-amber-500";

  // texte court
  let text = `${percent}%`;
  if (today.getTime() > e.getTime()) text = `Expiré • ${percent}%`;
  else if (today.getTime() < s.getTime())
    text = `Démarre bientôt • ${percent}%`;
  else if (Number.isFinite(daysRemaining))
    text = `J-${Math.max(0, daysRemaining)} • ${percent}%`;

  return { valid: true, percent, daysRemaining, colorCls, text };
};

export default function AbonnementPage() {
  useScrollToTop();

  const ASSO_ID = process.env.REACT_APP_ASSO_ID;

  const [abonnementData, setAbonnementData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAbonnement = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `https://api.monappliclub.com/api/subscriptions/subscriptionsByAssociation/${ASSO_ID}`,
      );
      setAbonnementData(Array.isArray(res.data) ? res.data : []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
      // toast.error("Erreur lors de la récupération des abonnements");
    } finally {
      setLoading(false);
    }
  }, [ASSO_ID]);

  useEffect(() => {
    fetchAbonnement();
  }, [fetchAbonnement]);

  const subs = useMemo(() => abonnementData ?? [], [abonnementData]);

  const kpis = useMemo(() => {
    const total = subs.length;
    const active = subs.filter(
      (s) => statusBadge(s?.status).label === "Abonné",
    ).length;
    const expired = subs.filter(
      (s) => statusBadge(s?.status).label === "Terminé",
    ).length;

    return { total, active, expired };
  }, [subs]);

  return (
    <div className="p-5 md:p-8 mt-[50px] space-y-6">
      {/* HERO */}
      <div className={`rounded-2xl p-6 shadow-sm ${GRADIENT}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Abonnement</h1>
            <p className="text-sm opacity-90 mt-1">
              Consultez votre formule, vos dates, votre statut et vos factures.
            </p>

            {lastRefresh && (
              <p className="text-xs opacity-80 mt-2">
                Dernière mise à jour :{" "}
                {lastRefresh.toLocaleString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={fetchAbonnement}
              className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-xl inline-flex items-center gap-2"
            >
              <FaSync /> Rafraîchir
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          <KpiCard label="Total" value={kpis.total} />
          <KpiCard label="Abonnés" value={kpis.active} />
          <KpiCard label="Terminés" value={kpis.expired} />
        </div>
      </div>

      {/* CONTENU */}
      <div className="rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        {loading ? (
          <SkeletonRows />
        ) : subs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="p-4 md:p-5">
            {/* TABLE DESKTOP */}
            <div className="hidden md:block">
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/60">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                      {[
                        "Produit",
                        "Montant €",
                        "Périodicité",
                        "Début",
                        "Fin",
                        "Statut",
                        "Progression",
                        "Facture",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                    {subs.map((sub) => {
                      const s = statusBadge(sub.status);
                      const per = periodicityBadge(sub.periodicity);
                      const prog = computeProgress(
                        sub.start_date,
                        sub.end_date,
                      );

                      return (
                        <tr
                          key={sub.id}
                          className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                        >
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                            {sub.product || "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                            {euro(sub.total_amount)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${per.cls}`}
                            >
                              {per.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {fmtDate(sub.start_date)}
                          </td>
                          <td className="px-4 py-3">{fmtDate(sub.end_date)}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${s.cls}`}
                            >
                              {s.label}
                            </span>
                          </td>

                          {/* ✅ Jauge */}
                          <td className="px-4 py-3">
                            <ProgressGauge prog={prog} />
                          </td>

                          <td className="px-4 py-3">
                            {sub.drive_invoice_link ? (
                              <a
                                href={sub.drive_invoice_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                              >
                                Voir la facture ↗
                              </a>
                            ) : (
                              <span className="italic text-slate-400">
                                Non disponible
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* CARTES MOBILE */}
            <div className="md:hidden space-y-3">
              {subs.map((sub) => {
                const s = statusBadge(sub.status);
                const per = periodicityBadge(sub.periodicity);
                const prog = computeProgress(sub.start_date, sub.end_date);

                return (
                  <div
                    key={`card-${sub.id}`}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/60 p-4 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                        {sub.product || "—"}
                      </h3>
                      <div className="flex items-center gap-1">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${per.cls}`}
                        >
                          {per.label}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${s.cls}`}
                        >
                          {s.label}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                      <div className="flex items-center justify-between">
                        <span className="opacity-70">Montant</span>
                        <span className="font-semibold">
                          {euro(sub.total_amount)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="opacity-70">Début</span>
                        <span>{fmtDate(sub.start_date)}</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="opacity-70">Fin</span>
                        <span>{fmtDate(sub.end_date)}</span>
                      </div>

                      {/* ✅ Jauge */}
                      <div className="mt-3">
                        <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                          Progression
                        </div>
                        <ProgressGauge prog={prog} />
                      </div>
                    </div>

                    <div className="mt-3">
                      {sub.drive_invoice_link ? (
                        <a
                          href={sub.drive_invoice_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          Voir la facture ↗
                        </a>
                      ) : (
                        <span className="italic text-slate-400">
                          Facture non disponible
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Parrainage */}
        <ParrainageSection />

        {/* Requests Admin */}
        <RequestsAdmin />
      </div>

      {/* <ToastContainer /> */}
    </div>
  );
}

function ProgressGauge({ prog }) {
  if (!prog?.valid) {
    return <span className="italic text-slate-400">—</span>;
  }

  return (
    <div className="min-w-[170px]">
      <div className="flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-300 mb-1">
        <span className="font-medium">{prog.text}</span>
        <span className="tabular-nums">{prog.percent}%</span>
      </div>

      <div className="h-2.5 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
        <div
          className={`h-full ${prog.colorCls} rounded-full transition-[width] duration-500`}
          style={{ width: `${prog.percent}%` }}
        />
      </div>
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="rounded-2xl p-4 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="text-xs text-slate-600 dark:text-slate-400">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-slate-900 dark:text-slate-50 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        📭
      </div>
      <div className="font-medium">Aucun abonnement trouvé.</div>
      <div className="text-sm text-slate-500 mt-1">
        Contactez le support si vous pensez que c’est une erreur.
      </div>
    </div>
  );
}